import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, Select, MenuItem,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { Visibility, Cancel } from '@mui/icons-material';

const AppointmentManagement = () => {
  const { adminUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [doctorNameFilter, setDoctorNameFilter] = useState('');
  const [patientNameFilter, setPatientNameFilter] = useState('');
  const [appointmentIdFilter, setAppointmentIdFilter] = useState(''); // New state for appointment ID filter
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [newDateTime, setNewDateTime] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!adminUser) {
        navigate('/admin');
        return;
      }
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      try {
        setLoading(true);
        const response = await api.get('/admin/appointments', config);
        setAppointments(response.data || []);
      } catch (err) {
        console.error('Appointments fetch error:', err.response?.data || err.message);
        setSnackbar({ open: true, message: 'Failed to fetch appointments', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchAppointments();
  }, [adminUser, navigate]);

  const handleCancel = async () => {
    if (!selectedAppointment || !cancelReason) return;
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    setIsProcessing(true);
    try {
      const response = await api.put(`/admin/appointments/${selectedAppointment._id}/cancel`, { reason: cancelReason }, config);
      setAppointments(appointments.map(app =>
        app._id === selectedAppointment._id ? { ...response.data } : app
      ));
      setSnackbar({ open: true, message: 'Appointment canceled successfully', severity: 'success' });
      setCancelDialogOpen(false);
      setCancelReason('');
      setSelectedAppointment(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || 'Unknown error occurred';
      console.error('Cancel error:', err.response?.data || err.message);
      setSnackbar({ open: true, message: `Failed to cancel appointment: ${errorMessage}`, severity: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedAppointment || !newDateTime || !rescheduleReason) return;
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    setIsProcessing(true);
    try {
      const response = await api.put(`/admin/appointments/${selectedAppointment._id}/reschedule`, 
        { newDate: newDateTime, reason: rescheduleReason }, 
        config
      );
      setAppointments(appointments.map(app =>
        app._id === selectedAppointment._id ? { ...response.data } : app
      ));
      setSnackbar({ open: true, message: 'Appointment rescheduled successfully', severity: 'success' });
      setRescheduleDialogOpen(false);
      setNewDateTime('');
      setRescheduleReason('');
      setSelectedAppointment(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || 'Unknown error occurred';
      console.error('Reschedule error:', err.response?.data || err.message);
      setSnackbar({ open: true, message: `Failed to reschedule appointment: ${errorMessage}`, severity: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredAppointments = appointments.filter(app => {
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesDate = dateFilter === '' || new Date(app.date).toLocaleDateString() === new Date(dateFilter).toLocaleDateString();
    const matchesDoctor = doctorNameFilter === '' || app.doctorId?.name?.toLowerCase().includes(doctorNameFilter.toLowerCase());
    const matchesPatient = patientNameFilter === '' || app.name?.toLowerCase().includes(patientNameFilter.toLowerCase());
    const matchesAppointmentId = appointmentIdFilter === '' || app._id.toString().toLowerCase().includes(appointmentIdFilter.toLowerCase()); // New filter for appointment ID
    return matchesStatus && matchesDate && matchesDoctor && matchesPatient && matchesAppointmentId;
  });

  const getStatusChip = (status) => {
    const statusStyles = {
      pending: { color: 'warning', label: 'Pending' },
      accepted: { color: 'primary', label: 'Accepted' },
      rejected: { color: 'default', label: 'Rejected' },
      rescheduled: { color: 'info', label: 'Rescheduled' },
      completed: { color: 'success', label: 'Completed' },
      canceled: { color: 'error', label: 'Canceled' },
    };
    const style = statusStyles[status] || { color: 'default', label: status };
    return <Chip label={style.label} color={style.color} size="small" />;
  };

  const getDisplayDate = (app) => {
    if (app.status === 'rescheduled' && app.rescheduledDate) {
      return `${new Date(app.rescheduledDate).toLocaleString()} (Rescheduled from ${new Date(app.date).toLocaleString()})`;
    }
    return new Date(app.date).toLocaleString();
  };

  const isActionDisabled = (status) => ['completed', 'canceled'].includes(status); // Removed 'rejected' from disabled list

  if (loading) return <Box display="flex" justifyContent="center"><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Appointment Management</Typography>
      <Paper sx={{ p: 2 }}>
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="rescheduled">Rescheduled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="canceled">Canceled</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Filter by Date"
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Filter by Doctor Name"
            value={doctorNameFilter}
            onChange={(e) => setDoctorNameFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Filter by Patient Name"
            value={patientNameFilter}
            onChange={(e) => setPatientNameFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Search by Appointment ID"
            value={appointmentIdFilter}
            onChange={(e) => setAppointmentIdFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          />
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Appointment ID</TableCell> {/* New column for Appointment ID */}
              <TableCell>Patient</TableCell>
              <TableCell>Doctor</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAppointments.map((app) => (
              <TableRow key={app._id}>
                <TableCell>{app._id}</TableCell> {/* Display Appointment ID */}
                <TableCell>{app.name || 'N/A'}</TableCell>
                <TableCell>{app.doctorId?.name || 'N/A'}</TableCell>
                <TableCell>{getDisplayDate(app)}</TableCell>
                <TableCell>{getStatusChip(app.status)}</TableCell>
                <TableCell>
                  {!isActionDisabled(app.status) && (
                    <>
                      <Button
                        onClick={() => {
                          setSelectedAppointment(app);
                          setCancelDialogOpen(true);
                        }}
                        color="error"
                        sx={{ mr: 1 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedAppointment(app);
                          setRescheduleDialogOpen(true);
                        }}
                        color="info"
                        sx={{ mr: 1 }}
                      >
                        Reschedule
                      </Button>
                    </>
                  )}
                  <Button startIcon={<Visibility />} onClick={() => navigate(`/admin/appointments/${app._id}`)}>
                    View Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredAppointments.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">No appointments found</TableCell> {/* Updated colSpan to 6 */}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason for Cancellation"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2 }}
            required
            disabled={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={isProcessing}>Close</Button>
          <Button onClick={handleCancel} color="error" disabled={!cancelReason || isProcessing}>
            {isProcessing ? <CircularProgress size={24} /> : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rescheduleDialogOpen} onClose={() => setRescheduleDialogOpen(false)}>
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Date & Time"
            type="datetime-local"
            value={newDateTime}
            onChange={(e) => setNewDateTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
            required
            disabled={isProcessing}
          />
          <TextField
            fullWidth
            label="Reason for Rescheduling"
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2 }}
            required
            disabled={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialogOpen(false)} disabled={isProcessing}>Close</Button>
          <Button onClick={handleReschedule} color="primary" disabled={!newDateTime || !rescheduleReason || isProcessing}>
            {isProcessing ? <CircularProgress size={24} /> : 'Confirm Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppointmentManagement;